import { useTranslation } from 'next-i18next';
import { useLocale } from '@/hooks';
import { getSubjectLocaleName } from '@/shared/category/getSubjectLocaleName';
import { PublisherLabelMap } from '@/shared/category/util';
import { Chip, LIST_ROW_CHIP_CLASS_NAME, SchoolInfoChip } from '@/shared/components/Chip/QuizInfoChip';
import { cn } from '@/utils/tailwind.util';
import type { FolderChip } from '../types/folder.types';
export type FolderChipsSize = 'card' | 'hero';

/**
 * 카드는 퀴즈 카드와 같은 목록 행 칩 크기를 쓴다.
 * 히어로는 `tablet-sm` 에서 패딩만 줄이고 폰트는 ZUS 560px 자동 축소에 맡긴다.
 */
const CHIP_TEXT_BY_SIZE: Record<FolderChipsSize, string> = {
  card: LIST_ROW_CHIP_CLASS_NAME,
  hero: 'text-body-sm-semibold max-tablet-sm:px-2xs'
};
interface FolderCategoryChipsProps {
  chips: FolderChip[];
  size?: FolderChipsSize;
  emptyPlaceholderClassName?: string;
  className?: string;
}
export const FolderCategoryChips = ({
  chips,
  size = 'card',
  emptyPlaceholderClassName,
  className
}: FolderCategoryChipsProps) => {
  const chipText = CHIP_TEXT_BY_SIZE[size];
  const {
    locale
  } = useLocale();
  if (chips.length === 0) {
    if (!emptyPlaceholderClassName) return null;
    return <div aria-hidden className={cn('flex flex-wrap gap-3xs', emptyPlaceholderClassName)}>
        <Chip className={cn(chipText, 'invisible')}>{'\u00A0'}</Chip>
      </div>;
  }
  return <div className={cn('flex flex-wrap gap-3xs', className)} data-sentry-component="FolderCategoryChips" data-sentry-source-file="FolderCategoryChips.tsx">
      {chips.map(chip => {
      if (chip.kind === 'SCHOOL') {
        return <SchoolInfoChip key={chip.kind} className={chipText} schoolInfo={{
          schoolType: chip.schoolType ?? '',
          schoolGrade: chip.schoolGrade ?? '',
          schoolSemester: chip.schoolSemester
        }} otherCount={chip.otherCount} />;
      }
      const label = chip.kind === 'SUBJECT' ? getSubjectLocaleName({
        defaultName: chip.name,
        localizeNames: chip.localizeNames,
        locale
      }) : PublisherLabelMap[chip.publisherId];
      if (!label) return null;
      return <Chip key={chip.kind} className={chipText}>
            <span>{label}</span>
            <OtherCount count={chip.otherCount} />
          </Chip>;
    })}
    </div>;
};
const OtherCount = ({
  count
}: {
  count: number;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return count > 0 ? <span className={'ml-4xs text-assistive'} data-sentry-component="OtherCount" data-sentry-source-file="FolderCategoryChips.tsx">
      {t('common:global.andMoreCount', {
      n: count
    })}
    </span> : null;
};